import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SlideMenu } from 'apps/sigma2-ui/src/components/SlideMenu';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { EditableName } from 'apps/sigma2-ui/src/components/dashboards/EditableName';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getStringValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { HorizontalFilterBarItem } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { getCustomVizFromFilters } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { getFiltersFromCustomViz } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { CircularProgress } from '@mui/material';
import { CreateOrUpdateDashboardChartDto } from 'libs/end-to-end/src/dashboard';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { getNextChartPosition } from 'libs/end-to-end/src/dashboard';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { RichTextEditorPure } from 'apps/sigma2-ui/src/components/rich-text/RichTextEditor';
import { Content } from '@tiptap/react';
import { StreamlitAppSelector } from 'apps/sigma2-ui/src/filters-v2/components/StreamlitAppSelector';
import { EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE } from 'libs/end-to-end/src/progress';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { v4 as uuid } from 'uuid';
import { deserializeRichTextContent } from 'apps/sigma2-ui/src/lib/custom-viz/rich-text';
import { PrimaryFilterButton } from 'apps/sigma2-ui/src/filters-v2/components/PrimaryFilterButton';
import { FilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { determinePossiblePrototypeStreamlitAppsForMyObservation } from 'libs/end-to-end/src/my-observation-utils';

const PROTOTYPE_STREAMLIT_APP_ID_KEY = 'prototypeStreamlitAppId';

export const EditDashboardChartSlideMenu = ({
  isOpen,
  setIsOpen,
  feature,
  saveChart,
  dashboardChart,
  dashboardId,
  dashboard,
}: {
  isOpen: boolean;
  setIsOpen: Setter<boolean>;
  feature: FiltersFeatureName;
  saveChart: (
    dashboardChart: CreateOrUpdateDashboardChartDto,
  ) => DraftDashboardChart | null;
  dashboardChart?: DraftDashboardChart;
  dashboardId: string;
  dashboard: DraftDashboard;
  // eslint-disable-next-line complexity
}) => {
  const language = useLanguage();
  const { myObservation, selectedOrganizationId } = useObservation();

  // rich text editor state
  const defaultContent = useMemo(() => {
    return dashboardChart?.customVizRequest?.customVizId === 'richText'
      ? deserializeRichTextContent(
          dashboardChart.customVizRequest.inputs.extras.richText
            .serializedContent,
        )
      : null;
  }, [dashboardChart]);

  const [content, setContent] = useState<Content>(defaultContent);

  // Streamlit app selector state
  const defaultStreamlitAppId = useMemo(() => {
    return dashboardChart?.customVizRequest?.customVizId === 'externalStreamlit'
      ? dashboardChart.customVizRequest.inputs.extras.streamlitAppId
      : null;
  }, [dashboardChart]);

  const [selectedStreamlitAppId, setSelectedStreamlitAppId] = useState<
    string | null
  >(defaultStreamlitAppId);

  const [editingChart, setEditingChart] = useState<DraftDashboardChart>(
    dashboardChart ?? {
      draftId: uuid(),
      name: '',
      description: '',
      dashboardId: dashboardId,
      viewConfiguration: {},
      width: 100,
      height: null,
      defaultViewMode: 'table',
      selectedMetric: null,
      baseColors: null,
      customVizRequest: {
        customVizId: 'richText',
        organizationId: uuid(),
        observationLocationType: {
          type: 'db-revision',
          databaseId: 'db-id',
          databaseRevisionId: 'rev-id',
        },
        inputs: {
          dropdowns: {},
          extras: {
            richText: {
              serializedContent: 'content',
              plainText: 'text',
            },
          },
        },
      },
      ...getNextChartPosition(dashboard.charts),
    },
  );

  const editingChartCustomVizId = useMemo(() => {
    return editingChart?.customVizRequest?.customVizId ?? null;
  }, [editingChart]);

  const possiblePrototypeStreamlitApps = useMemo(() => {
    return determinePossiblePrototypeStreamlitAppsForMyObservation({
      customVizId: editingChartCustomVizId,
      myObservation,
    });
  }, [editingChartCustomVizId, myObservation]);

  const editingPrototypeStreamlitAppId = useMemo(() => {
    const current =
      editingChart.viewConfiguration.streamlitPrototypeAppId ?? null;
    return current !== null &&
      possiblePrototypeStreamlitApps.some((app) => app.id === current)
      ? current
      : null;
  }, [
    editingChart.viewConfiguration.streamlitPrototypeAppId,
    possiblePrototypeStreamlitApps,
  ]);

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const overridePrimaryFilterKeys = useMemo(() => {
    if (feature === 'employeeLifecycle') {
      return ['pipelineAnalysisConfiguration', 'dimension'];
    }

    if (feature === 'dataIntelligenceDeepDive') {
      return ['focusArea', 'employeeGroup', 'comparisonGroup'];
    }

    if (feature === 'binScatterMultiSelect') {
      return ['focusArea', 'dimension'];
    }

    return null;
  }, [feature]);

  const updateCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      extras,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
      extras?: Record<string, unknown>;
      // eslint-disable-next-line complexity
    }) => {
      if (!myObservation || !selectedOrganizationId) {
        return;
      }

      // We allow mutations below because we are creating a new object in getCustomVizFromFilters
      const viz: CustomVizRequest | null = getCustomVizFromFilters({
        feature,
        primaryFilterSelections,
        secondaryFilterSelections,
        myObservation,
        organizationId: selectedOrganizationId,
        extras,
      });

      if (!viz) {
        return;
      }

      // persist visible departments for table view. this filter is only visible on the explore view so we have to persist it here.
      if (
        viz &&
        viz?.customVizId === 'tableView' &&
        dashboardChart?.customVizRequest?.customVizId === 'tableView'
      ) {
        viz.inputs.extras.visibleDepartments =
          dashboardChart?.customVizRequest?.inputs.extras.visibleDepartments;
      }

      const newName = getTranslatedContentAsString(
        language.savedAnalysisCardTitle({
          myObservation,
          request: viz,
        }),
      );

      setEditingChart((chart) => ({
        ...chart,
        customVizRequest: viz,
        name: chart.name || newName,
      }));
    },
    [
      dashboardChart?.customVizRequest?.customVizId,
      dashboardChart?.customVizRequest?.inputs.extras,
      feature,
      language,
      myObservation,
      selectedOrganizationId,
    ],
  );

  const filtersProps = useMemo(() => {
    const filters =
      dashboardChart && myObservation
        ? getFiltersFromCustomViz({
            customVizRequest: dashboardChart?.customVizRequest,
            myObservation,
          })
        : undefined;

    const focusArea: FocusArea | null =
      feature === 'successFactors'
        ? 'Performance'
        : (filters?.focusArea ?? null);

    return {
      feature,
      // success factors is limited to performance focus area
      focusArea,
      analysisName: undefined,
      disabled: false,
      metricDefinitionWhiteLabeling: null,
      onApply: updateCustomVizRequest,
      overridePrimaryFilterKeys:
        overridePrimaryFilterKeys as PrimaryFilterKey[],
      dashboardGlobalFilters: dashboard.globalFilters,
    };
  }, [
    dashboardChart,
    myObservation,
    feature,
    updateCustomVizRequest,
    overridePrimaryFilterKeys,
    dashboard.globalFilters,
  ]);

  const filterPayload = useFilters(filtersProps);
  const [filtersReady, setFiltersReady] = useState(false);

  const filteredItems = useMemo(() => {
    if (feature === 'timeSeriesTrendsPrimary') {
      const treatmentDate = getStringValueFromPrimaryFilterValues({
        primaryFilterKey: 'singleDate',
        primaryFilterValues: filterPayload.primaryFilterSelections,
      });

      if (
        treatmentDate &&
        treatmentDate !== EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE
      ) {
        return filterPayload.items.filter(
          (item) =>
            !['primary:chartType', 'primary:bigTimePeriodComparison'].includes(
              item.key,
            ),
        );
      }
    }
    return filterPayload.items;
  }, [filterPayload.items, feature, filterPayload.primaryFilterSelections]);

  // clear filters when we open the menu
  useEffect(() => {
    if (!dashboardChart) {
      uiAppLogger.info('Resetting filters to initial state');
      filterPayload.resetFiltersToInitialState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update filters to match the dashboard chart
  useEffect(() => {
    if (
      !dashboardChart?.customVizRequest ||
      !myObservation ||
      !filterPayload.isInitialized
    ) {
      return;
    }
    const filters = getFiltersFromCustomViz({
      customVizRequest: dashboardChart.customVizRequest,
      myObservation,
    });

    if (filters && filterPayload.isInitialized) {
      uiAppLogger.info('Setting filters from dashboard chart:', filters);
      filterPayload.setFilters(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPayload.isInitialized, myObservation]);

  const [openedItem, setOpenedItem] = React.useState<string | null>(null);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Handle Streamlit app selection change
  const handleStreamlitAppChange = useCallback(
    (appId: string) => {
      setSelectedStreamlitAppId(appId);
      if (feature === 'externalStreamlit') {
        updateCustomVizRequest({
          primaryFilterSelections: [],
          secondaryFilterSelections: [],
          extras: {
            streamlitAppId: appId,
          },
        });
      }
    },
    [feature, updateCustomVizRequest],
  );

  const handleSaveChart = useCallback(() => {
    setIsSaving(true);

    const response = saveChart(editingChart);

    if (response !== null) {
      setIsOpen(false);
    }

    setIsSaving(false);
  }, [editingChart, saveChart, setIsOpen]);

  const contentSerialized = useMemo(() => JSON.stringify(content), [content]);

  const lastIsInitialized = useRef<boolean>(false);

  useEffect(() => {
    if (lastIsInitialized.current === false && filterPayload.isInitialized) {
      uiAppLogger.info('Manually applying filters');

      filterPayload.manualApply();
      setFiltersReady(true);
    }

    lastIsInitialized.current = filterPayload.isInitialized;
  }, [filterPayload.isInitialized, filterPayload]);

  if (!filtersReady) {
    return null;
  }

  return (
    <SlideMenu
      width="w-1/3"
      show={isOpen}
      onClose={() => setIsOpen(false)}
      qaDataName="slide-menu-edit"
    >
      <div data-testid="dashboard-fob-button-slide-menu">
        <div className="sticky -top-6 -m-6">
          <div className="w-full bg-gray-50 px-6 h-16 flex border-gray-200 border-b-1 border-solid ">
            <Spacing
              direction="horizontal"
              size=""
              className="justify-between w-full"
            >
              <Spacing
                direction="horizontal"
                size="xs"
                className="items-center grow "
              >
                <EditableName
                  name={editingChart.name ?? ''}
                  size="headline-5"
                  onAfterSave={(name) =>
                    setEditingChart((chart) => ({ ...chart, name }))
                  }
                />
              </Spacing>
              <Spacing
                direction="horizontal"
                size="md"
                className="items-center px-4 text-white"
              >
                <PrimaryButtonLightBg
                  onClick={handleSaveChart}
                  disabled={
                    !editingChart.customVizRequest ||
                    (editingChart.customVizRequest?.customVizId ===
                      'richText' &&
                      !editingChart.customVizRequest.inputs.extras.richText?.plainText?.trim())
                  }
                  translatedContent={language.save()}
                  icon={() =>
                    isSaving && (
                      <CircularProgress
                        size={16}
                        thickness={4}
                        color={'inherit'}
                      />
                    )
                  }
                />
              </Spacing>
            </Spacing>
          </div>
        </div>
        <div className="pt-12 ">
          <div className="flex flex-col divide-y">
            {feature !== 'richText' && (
              <div className="flex flex-col gap-3 pb-5">
                <S2Typography
                  variant="body-6"
                  translatedContent={language.description()}
                />
                <textarea
                  ref={textAreaRef}
                  className="form-textarea border-none !ring-0 resize-none p-0"
                  placeholder={language.addADescription()}
                  style={{
                    height: `${textAreaRef.current?.scrollHeight ?? 34}px`,
                  }}
                  onChange={(e) =>
                    setEditingChart((chart) => ({
                      ...chart,
                      description: e.target.value,
                    }))
                  }
                  value={editingChart.description ?? ''}
                ></textarea>
              </div>
            )}
            <div className="flex flex-col gap-3 py-5">
              {feature !== 'richText' && feature !== 'externalStreamlit' && (
                <S2Typography
                  variant="body-6"
                  translatedContent={language.filters()}
                />
              )}
              {feature === 'externalStreamlit' && (
                <div className="flex flex-col gap-3">
                  <S2Typography
                    variant="body-6"
                    translatedContent="Select Streamlit App"
                  />
                  <StreamlitAppSelector
                    selectedStreamlitAppId={selectedStreamlitAppId}
                    onSelectStreamlitApp={handleStreamlitAppChange}
                  />
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <>
                  {filteredItems.map((item) => (
                    <HorizontalFilterBarItem
                      {...item}
                      key={item.key}
                      openMenu={() => setOpenedItem(item.key)}
                      closeMenu={() => setOpenedItem(null)}
                      menuOpened={openedItem === item.key}
                    />
                  ))}
                  {possiblePrototypeStreamlitApps.length > 0 && (
                    <HorizontalFilterBarItem
                      menu={({ closeMenu }) => {
                        return (
                          <FilterMenuSinglePanel
                            filterContent={
                              <FilterListBox
                                multiple={false}
                                items={[
                                  {
                                    translatedContent:
                                      language.prototypeStreamlitAppIdNoneSelected(),
                                    value: '',
                                    selected:
                                      editingPrototypeStreamlitAppId === null,
                                  },
                                  ...possiblePrototypeStreamlitApps.map(
                                    (app) => {
                                      return {
                                        translatedContent: app.name,
                                        value: app.id,
                                        selected:
                                          app.id ===
                                          editingPrototypeStreamlitAppId,
                                      };
                                    },
                                  ),
                                ]}
                                onUpdate={({ addedSelections }) => {
                                  const newStreamlitAppId =
                                    addedSelections[0] ?? null;
                                  setEditingChart((chart) => ({
                                    ...chart,
                                    viewConfiguration: {
                                      ...chart.viewConfiguration,
                                      streamlitPrototypeAppId:
                                        !newStreamlitAppId
                                          ? undefined
                                          : newStreamlitAppId,
                                    },
                                  }));

                                  closeMenu();
                                }}
                              />
                            }
                            bottomProps={'none'}
                          />
                        );
                      }}
                      content={({ openMenu, menuOpened }) => {
                        return (
                          <PrimaryFilterButton
                            onClick={() => {
                              openMenu();
                            }}
                            disabled={false}
                            active={menuOpened}
                            hasSelection={
                              editingPrototypeStreamlitAppId !== null
                            }
                            leftTranslatedContent={language.prototypeStreamlitAppIdLabel()}
                            rightTranslatedContent={
                              editingPrototypeStreamlitAppId === null
                                ? language.prototypeStreamlitAppIdNoneSelected()
                                : (possiblePrototypeStreamlitApps.find(
                                    (app) =>
                                      app.id === editingPrototypeStreamlitAppId,
                                  )?.name ?? '')
                            }
                          />
                        );
                      }}
                      key={PROTOTYPE_STREAMLIT_APP_ID_KEY}
                      openMenu={() =>
                        setOpenedItem(PROTOTYPE_STREAMLIT_APP_ID_KEY)
                      }
                      closeMenu={() => setOpenedItem(null)}
                      menuOpened={openedItem === PROTOTYPE_STREAMLIT_APP_ID_KEY}
                    />
                  )}
                </>
              </div>
              {feature === 'richText' && (
                <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
                  <RichTextEditorPure
                    content={contentSerialized}
                    isLoading={false}
                    isEditable={true}
                    onApply={(editor) => {
                      setContent(editor.getJSON());
                      updateCustomVizRequest({
                        primaryFilterSelections: [],
                        secondaryFilterSelections: [],
                        extras: {
                          richText: {
                            serializedContent: JSON.stringify(editor.getJSON()),
                            plainText: editor.getText() ?? '',
                          },
                        },
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SlideMenu>
  );
};
